import { Avatar, AvatarFallback, AvatarImage } from '@journ/ui/avatar'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import type { ColumnDef } from '@/libs/table'
import { actionsColumnBase, stickyNameClass, stickyNameCellClass } from '@/components/table-styles'
import type { ColumnMeta } from '@/components/table-styles'
import { memo, useMemo } from 'react'
import { useIntlayer } from 'react-intlayer'
import type { Customer } from '../domain/customer'
import { openSheet } from '@/features/ui/store'

type CustomerColumnMeta = ColumnMeta & {
	skeleton?: { type: 'avatar-text' | 'text' | 'icon'; width?: string }
}

type CustomerTableMeta = {
	deleteCustomer?: (id: string) => void
	enrichCustomer?: (id: string) => void
	cancelEnrichment?: (id: string) => void
	clearEnrichment?: (id: string) => void
}

const NameCell = memo(({ name, logoUrl }: { name: string | null; logoUrl: string | null }) => {
	if (!name) return <span>-</span>
	return (
		<div className='flex items-center space-x-2'>
			<Avatar className='size-5'>
				<AvatarImage src={logoUrl ?? ''} alt='' className='object-cover' />
				<AvatarFallback className='text-[9px] font-medium'>{name[0]}</AvatarFallback>
			</Avatar>
			<span className='truncate'>{name}</span>
		</div>
	)
})
NameCell.displayName = 'NameCell'

const WebsiteCell = memo(({ website }: { website: string | null }) => {
	if (!website) return <span>-</span>
	const display = website.replace(/^https?:\/\//, '').replace(/\/$/, '')
	return (
		<a
			href={website.startsWith('http') ? website : `https://${website}`}
			target='_blank'
			rel='noopener noreferrer'
			className='text-primary hover:underline truncate block'
			onClick={e => e.stopPropagation()}
		>
			{display}
		</a>
	)
})
WebsiteCell.displayName = 'WebsiteCell'

const ActionsCell = memo(
	({
		customer,
		onDelete,
		onEnrich,
		onCancelEnrichment,
		onClearEnrichment,
	}: {
		customer: Customer
		onDelete?: (id: string) => void
		onEnrich?: (id: string) => void
		onCancelEnrichment?: (id: string) => void
		onClearEnrichment?: (id: string) => void
	}) => {
		const content = useIntlayer('customers-page')
		const isEnriching = customer.enrichmentStatus === 'pending' || customer.enrichmentStatus === 'processing'
		const hasEnrichmentData = Boolean(customer.description || customer.industry || customer.companyType)
		const canEnrich = Boolean(customer.website || customer.email)

		const handleEdit = () => openSheet('customer-edit', customer.id)

		return (
			<div className='flex items-center justify-center w-full'>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' className='h-8 w-8 p-0'>
							<Icons.MoreHoriz className='h-4 w-4' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						<DropdownMenuItem onClick={handleEdit}>{content.actions.edit}</DropdownMenuItem>
						{isEnriching ? (
							<DropdownMenuItem onClick={() => onCancelEnrichment?.(customer.id)}>
								{content.actions.cancelEnrichment}
							</DropdownMenuItem>
						) : (
							canEnrich && (
								<DropdownMenuItem onClick={() => onEnrich?.(customer.id)}>
									{hasEnrichmentData ? content.actions.refreshEnrichment : content.actions.enrich}
								</DropdownMenuItem>
							)
						)}
						{hasEnrichmentData && !isEnriching && (
							<DropdownMenuItem onClick={() => onClearEnrichment?.(customer.id)}>
								{content.actions.clearEnrichment}
							</DropdownMenuItem>
						)}
						<DropdownMenuItem onClick={() => onDelete?.(customer.id)} className='text-FF3638'>
							{content.actions.delete}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		)
	},
)
ActionsCell.displayName = 'ActionsCell'

const EnrichmentCell = memo(({ customer }: { customer: Customer }) => {
	const content = useIntlayer('customers-page')
	const status = customer.enrichmentStatus
	if (!status) return <span className='text-878787'>-</span>
	const label = content.enrichment[status]
	const variant = status === 'failed' ? 'destructive' : status === 'completed' ? 'default' : 'secondary'
	return <Badge variant={variant}>{label}</Badge>
})
EnrichmentCell.displayName = 'EnrichmentCell'

const useCustomerColumns = () => {
	const content = useIntlayer('customers-page')
	const c = content.columns

	return useMemo<ColumnDef<Customer, unknown>[]>(
		() => [
			{
				id: 'name',
				accessorKey: 'name',
				header: c.name.value,
				size: 280,
				minSize: 200,
				enableResizing: true,
				meta: {
					headerLabel: c.name.value,
					sticky: true,
					skeleton: { type: 'avatar-text', width: 'w-32' },
					headerClassName: `w-[280px] min-w-[200px] md:sticky md:left-0 bg-background z-10 ${stickyNameClass}`,
					className: `w-[280px] min-w-[200px] md:sticky md:left-0 bg-background group-hover:bg-hover-group  z-10 ${stickyNameCellClass}`,
				} satisfies CustomerColumnMeta,
				cell: ({ row }) => <NameCell name={row.original.name} logoUrl={row.original.logoUrl} />,
			},
			{
				id: 'contact',
				accessorKey: 'contact',
				header: c.contact.value,
				size: 220,
				minSize: 160,
				enableResizing: true,
				meta: {
					headerLabel: c.contact.value,
					skeleton: { type: 'text', width: 'w-24' },
					className: 'w-[220px] min-w-[160px]',
				} satisfies CustomerColumnMeta,
				cell: ({ row }) => <span>{row.original.contact ?? '-'}</span>,
			},
			{
				id: 'email',
				accessorKey: 'email',
				header: c.email.value,
				size: 280,
				minSize: 200,
				enableResizing: true,
				meta: {
					headerLabel: c.email.value,
					skeleton: { type: 'text', width: 'w-32' },
					className: 'w-[280px] min-w-[200px]',
				} satisfies CustomerColumnMeta,
				cell: ({ row }) => <span>{row.original.email}</span>,
			},
			{
				id: 'website',
				accessorKey: 'website',
				header: c.website.value,
				size: 180,
				minSize: 140,
				enableResizing: true,
				meta: {
					headerLabel: c.website.value,
					skeleton: { type: 'text', width: 'w-24' },
					className: 'w-[180px] min-w-[140px]',
				} satisfies CustomerColumnMeta,
				cell: ({ row }) => <WebsiteCell website={row.original.website} />,
			},
			{
				id: 'country',
				accessorKey: 'country',
				header: c.country.value,
				size: 150,
				minSize: 100,
				enableResizing: true,
				meta: {
					headerLabel: c.country.value,
					skeleton: { type: 'text', width: 'w-16' },
					className: 'w-[150px] min-w-[100px]',
				} satisfies CustomerColumnMeta,
				cell: ({ row }) => <span>{row.original.country ?? '-'}</span>,
			},
			{
				id: 'enrichment',
				accessorKey: 'enrichmentStatus',
				header: c.enrichment.value,
				size: 160,
				minSize: 130,
				enableResizing: true,
				meta: {
					headerLabel: c.enrichment.value,
					skeleton: { type: 'text', width: 'w-20' },
					className: 'w-[160px] min-w-[130px]',
				} satisfies CustomerColumnMeta,
				cell: ({ row }) => <EnrichmentCell customer={row.original} />,
			},
			{
				...actionsColumnBase,
				header: c.actions.value,
				meta: {
					...actionsColumnBase.meta,
					headerLabel: c.actions.value,
					skeleton: { type: 'icon' as const },
				} satisfies CustomerColumnMeta,
				cell: ({ row, table }) => {
					const meta = table.options.meta as CustomerTableMeta | undefined
					return (
						<ActionsCell
							customer={row.original}
							onDelete={meta?.deleteCustomer}
							onEnrich={meta?.enrichCustomer}
							onCancelEnrichment={meta?.cancelEnrichment}
							onClearEnrichment={meta?.clearEnrichment}
						/>
					)
				},
			},
		],
		[c],
	)
}

export { useCustomerColumns }
export type { CustomerColumnMeta as ColumnMeta }
